// src/TermsOfService.jsx
import React from 'react';
import NavigationBar from './NavigationBar';
import './PolicyPages.css';

const TermsOfService = () => {
  return (
    <>
      <NavigationBar />
      <div className="policy-page-container">
        <div className="policy-content">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>

          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using TickrTrends ("the Service"), you accept and agree to be bound by these Terms of Service. 
              If you do not agree to all terms and conditions, you must immediately discontinue use of the Service.
              Your continued use constitutes acceptance of any modifications to these terms.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              TickrTrends is a <strong>data aggregation and analysis platform</strong> that provides stock market information, 
              analysis, and insights derived from publicly available data and AI-powered algorithms. Our services include:
            </p>
            <ul>
              <li>Aggregated stock market data from third-party sources</li>
              <li>Proprietary valuation models and screening tools</li>
              <li>AI-generated market analysis and commentary</li>
              <li>Momentum tracking and technical indicators</li>
              <li>Educational content about investing strategies</li>
            </ul>
            <p>
              <strong>The Service is a software tool for informational and educational purposes only.</strong>
            </p>
          </section>

          <section>
            <h2>3. Critical Investment Disclaimers</h2>
            
            <h3>3.1 Not Investment Advice</h3>
            <p>
              <strong>IMPORTANT - READ CAREFULLY:</strong>
            </p>
            <ul>
              <li>TickrTrends is NOT a registered investment advisor, broker-dealer, or financial planner</li>
              <li>We do NOT provide personalized investment advice or recommendations</li>
              <li>All content is general educational information and market commentary</li>
              <li>Nothing on this platform constitutes a recommendation to buy, sell, or hold any security</li>
              <li>We do NOT have a fiduciary duty to you</li>
              <li>You are solely responsible for your own investment decisions</li>
            </ul>

            <h3>3.2 No Guarantees or Warranties</h3>
            <p>
              <strong>We make NO guarantees about:</strong>
            </p>
            <ul>
              <li>Accuracy, completeness, or timeliness of any data or analysis</li>
              <li>Performance, profitability, or success of any investment strategy</li>
              <li>Suitability of any information for your specific situation</li>
              <li>Availability, reliability, or functionality of the Service</li>
            </ul>

            <h3>3.3 Third-Party Data</h3>
            <p>
              Our platform aggregates data from third-party sources including Yahoo Finance, public APIs, and AI models. 
              We are NOT responsible for errors, delays, or inaccuracies in third-party data. All data is provided 
              "AS IS" without warranty of any kind.
            </p>

            <h3>3.4 Investment Risks</h3>
            <p>
              <strong>You acknowledge that:</strong>
            </p>
            <ul>
              <li>All investments involve risk, including total loss of principal</li>
              <li>Past performance does NOT indicate future results</li>
              <li>Markets are unpredictable and volatile</li>
              <li>You should consult a licensed financial advisor before making investment decisions</li>
              <li>You are using this Service at your own risk</li>
            </ul>
          </section>

          <section>
            <h2>4. Subscription and Payment</h2>
            <p>
              Subscription fee: $4.99/month, charged monthly on a recurring basis. By subscribing:
            </p>
            <ul>
              <li>You authorize automatic recurring charges until cancellation</li>
              <li>Fees are non-refundable except as stated in our Refund Policy</li>
              <li>We may change pricing with 30 days' notice</li>
              <li>Failed payments may result in immediate service suspension</li>
              <li>You are responsible for all applicable taxes</li>
            </ul>
          </section>

          <section>
            <h2>5. User Conduct and Restrictions</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Share your account credentials with others</li>
              <li>Use the Service for any illegal purpose</li>
              <li>Scrape, data mine, or extract data through automated means</li>
              <li>Reverse engineer or decompile any part of the Service</li>
              <li>Resell, redistribute, or republish our content</li>
              <li>Circumvent any access restrictions or security measures</li>
              <li>Interfere with the Service's operation or other users' access</li>
            </ul>
            <p>
              Violations may result in immediate account termination without refund.
            </p>
          </section>

          <section>
            <h2>6. Intellectual Property</h2>
            <p>
              All content, analysis, algorithms, software, graphics, and data presentations are the exclusive 
              property of TickrTrends and protected by copyright and intellectual property laws. 
              You receive only a limited, non-exclusive, non-transferable license to access the Service 
              for personal use. All rights not expressly granted are reserved.
            </p>
          </section>

          <section>
            <h2>7. Limitation of Liability</h2>
            <p>
              <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW:</strong>
            </p>
            <ul>
              <li>TickrTrends, its owners, officers, employees, and affiliates are NOT liable for any losses, 
              damages, or consequences resulting from your use of the Service</li>
              <li>We are NOT liable for investment losses, missed opportunities, or financial damages of any kind</li>
              <li>We are NOT liable for errors, omissions, interruptions, or delays in the Service</li>
              <li>Our total liability to you shall not exceed the fees you paid in the past 3 months</li>
              <li>We are NOT liable for indirect, incidental, consequential, or punitive damages</li>
            </ul>
          </section>

          <section>
            <h2>8. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless TickrTrends and its affiliates from any claims, 
              damages, losses, or expenses (including legal fees) arising from:
            </p>
            <ul>
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your investment decisions or financial losses</li>
              <li>Any claim that you relied on our content as investment advice</li>
            </ul>
          </section>

          <section>
            <h2>9. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul>
              <li>Warranties of merchantability or fitness for a particular purpose</li>
              <li>Warranties of accuracy, reliability, or completeness</li>
              <li>Warranties of uninterrupted or error-free service</li>
              <li>Warranties that defects will be corrected</li>
            </ul>
          </section>

          <section>
            <h2>10. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account immediately, without notice or refund, 
              for any reason including:
            </p>
            <ul>
              <li>Violation of these Terms</li>
              <li>Fraudulent or abusive behavior</li>
              <li>Payment failures</li>
              <li>At our sole discretion for any reason</li>
            </ul>
            <p>
              Upon termination, your right to use the Service immediately ceases.
            </p>
          </section>

          <section>
            <h2>11. Dispute Resolution and Arbitration</h2>
            <p>
              <strong>PLEASE READ THIS SECTION CAREFULLY - IT AFFECTS YOUR LEGAL RIGHTS.</strong>
            </p>
            <p>
              Any dispute arising from these Terms or your use of the Service shall be resolved through 
              binding arbitration rather than in court, except you may assert claims in small claims court 
              if they qualify. You waive your right to a jury trial and to participate in class actions.
            </p>
            <p>
              Arbitration shall be conducted by the American Arbitration Association (AAA) under its 
              Commercial Arbitration Rules. The arbitrator's decision is final and binding.
            </p>
          </section>

          <section>
            <h2>12. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the State of Mississippi, United States, 
              without regard to conflict of law principles. Venue for any disputes shall be in Mississippi.
            </p>
          </section>

          <section>
            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be communicated 
              via email or in-app notification. Continued use after changes constitutes acceptance. 
              If you do not agree to changes, you must cancel your subscription.
            </p>
          </section>

          <section>
            <h2>14. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining provisions 
              shall remain in full force and effect.
            </p>
          </section>

          <section>
            <h2>15. Entire Agreement</h2>
            <p>
              These Terms, together with our Privacy Policy and Refund Policy, constitute the entire 
              agreement between you and TickrTrends regarding use of the Service.
            </p>
          </section>

          <section>
            <h2>16. Contact Information</h2>
            <p>
              Questions about these Terms? Contact us at:
              <br />
              <strong>Email:</strong> ryan@tickrtrends.com
            </p>
          </section>

          <section style={{ 
            background: 'rgba(245, 158, 11, 0.1)', 
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '40px'
          }}>
            <h2 style={{ color: '#f59e0b' }}>⚠️ Final Reminder</h2>
            <p>
              <strong>TickrTrends is a data aggregation and analysis tool, NOT an investment advisor.</strong> 
              We provide educational information and market data. You are solely responsible for your investment 
              decisions. Consult a licensed financial professional before investing. All investments carry risk.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default TermsOfService;